import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import Information from 'features/bpDetails/Components/BpInfo/Information'
import Activity from 'features/bpDetails/Components/BpInfo/Activity'
import { useTranslation } from 'react-i18next'
import { isNotNilOrEmpty } from 'utils/ramda'
import Modal from 'components/atoms/Modal'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import EditAddress from 'features/bpDetails/Components/edit/EditAddress'
import EditGeneralInfo from 'features/bpDetails/Components/edit/EditGeneralInfo'
import EditBuildingTypes from 'features/bpDetails/Components/edit/EditBuildingTypes'
import EditFloors from 'features/bpDetails/Components/edit/EditFloors'
import EditResidents from 'features/bpDetails/Components/edit/EditResidents'
import EditIssues from 'features/bpDetails/Components/edit/EditIssues'
import { useSelector } from 'react-redux'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import AddAssignmentModal from 'features/accessControl/components/AddAssignmentModal'
import { canEditBp } from 'utils/user'
import { getSelectedBp, selectHasAccessToBp } from 'features/bpDetails/ducks/selectors'

const BpInfo = ({ redirectPath }) => {
  const bp = useSelector(getSelectedBp)
  const [activeTab, setActiveTab] = useState('information')
  const [modalOpen, setModalOpen] = useState(false)
  const [editContentName, setEditContentName] = useState('')
  const currentUser = useSelector(getCurrentUser)
  const hasFullAccess = useSelector(selectHasAccessToBp)
  const canEdit = canEditBp(currentUser, bp)
  const { t } = useTranslation()

  const setTab = tabName => () => setActiveTab(tabName)

  const closeEditModal = () => {
    setEditContentName('')
    setModalOpen(false)
  }

  const openEditModal = name => {
    setEditContentName(name)
    setModalOpen(true)
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'activity':
        return <Activity />
      case 'information':
      default:
        return (
          <Information openEditModal={openEditModal} redirectPath={redirectPath} />
        )
    }
  }

  const openGeneralInfoEdit = () => {
    openEditModal('general')
  }

  const modalContent = useMemo(() => {
    switch (editContentName) {
      case 'general':
        return <EditGeneralInfo closeModal={closeEditModal} />
      case 'address':
        return <EditAddress closeModal={closeEditModal} />
      case 'buildingTypes':
        return <EditBuildingTypes closeModal={closeEditModal} />
      case 'floors':
        return <EditFloors closeModal={closeEditModal} />
      case 'residents':
        return <EditResidents closeModal={closeEditModal} />
      case 'issues':
        return <EditIssues closeModal={closeEditModal} />
      case 'owner':
        return <AddAssignmentModal initialUserType='owner' closeModal={closeEditModal} />
      case 'responsiblePerson':
        return <AddAssignmentModal initialUserType='responsible_person' closeModal={closeEditModal} />
      case 'principalAccountablePerson':
        return <AddAssignmentModal initialUserType='principal_accountable_person' closeModal={closeEditModal} />
      case 'principalDesigner':
        return <AddAssignmentModal initialUserType='principal_designer' closeModal={closeEditModal} />
      default:
        return <div />
    }
  }, [editContentName])

  return (
    <Wrapper>
      <StickyTop>
        <Header>
          <TitleWrapper>
            <Icon>
              <DescriptionOutlinedIcon />
            </Icon>

            <TitleText>
              <BpName title={bp?.name || ''}>{bp?.name}</BpName>
              <Uprn>
                UPRN:{' '}
                {isNotNilOrEmpty(bp?.uprn) ? bp.uprn : 'Not yet allocated'}
              </Uprn>
            </TitleText>
          </TitleWrapper>

          {canEdit && (
            <EditIconWrapper onClick={openGeneralInfoEdit} aria-label='Edit'>
              <EditIcon />
            </EditIconWrapper>
          )}
        </Header>

        {hasFullAccess && (
          <TabsWrapper>
            <Tab
              onClick={setTab('information')}
              isactive={(activeTab === 'information').toString()}
              role='button'
            >
              {t('bpDetails.tabs.information')}
            </Tab>
            <Tab
              onClick={setTab('activity')}
              isactive={(activeTab === 'activity').toString()}
              role='button'
            >
              {t('bpDetails.tabs.activity')}
            </Tab>
          </TabsWrapper>
        )}
      </StickyTop>

      <ContentWrapper>
        {renderTabContent()}
      </ContentWrapper>

      <Modal open={modalOpen} title='Edit' onClose={closeEditModal}>
        {modalContent}
      </Modal>
    </Wrapper>
  )
}

export default BpInfo

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const StickyTop = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  background: ${({ theme }) => theme.colors.lighterGrey};
  padding-top: env(safe-area-inset-top);
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  padding: 18px 0 10px 0;

  @media (max-width: 768px) {
    padding: 12px 0 8px 0;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  gap: 12px;
  min-width: 0;
  padding-right: 52px;

  @media (max-width: 768px) {
    gap: 10px;
    padding-right: 48px;
  }
`

const TitleText = styled.div`
  min-width: 0;
`

const Icon = styled.div`
  min-width: 40px;
  min-height: 40px;
  max-width: 40px;
  max-height: 40px;
  background-color: ${({ theme }) => theme.colors.linen};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 30px !important;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 10px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: 36px;
    min-height: 36px;
    max-width: 36px;
    max-height: 36px;
    border-radius: 12px;
  }
`

const BpName = styled.div`
  font-size: 19px;
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.haiti};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const Uprn = styled.div`
  font-size: 12px;
  margin-top: 4px;
  text-transform: uppercase;
  opacity: 0.5;
  word-break: break-all;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const TabsWrapper = styled.div`
  width: 100%;
  display: flex;
  margin-top: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ theme }) => theme.colors.lighterGrey};

  & > div {
    flex: 1;
  }
`

const Tab = styled.div`
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  font-size: 14px;
  user-select: none;

  border-bottom: 3px solid
    ${({ theme, isactive }) =>
  isactive === 'true' ? theme.colors.secondary : 'transparent'};

  color: ${({ theme, isactive }) =>
  isactive === 'true' ? theme.colors.haiti : theme.colors.grey[700]};

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 12px 0;
  }
`

const ContentWrapper = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 0 18px 0;
  -webkit-overflow-scrolling: touch;
`

const EditIconWrapper = styled.button`
  position: absolute;
  top: 12px;
  right: 0;

  width: 44px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 14px;

  cursor: pointer;
  padding: 0;

  @media (min-width: 769px) {
    top: 14px;
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }
`

const EditIcon = styled(CreateOutlinedIcon)`
  font-size: 20px !important;
  color: ${({ theme }) => theme.colors.secondary};
`
